import React, { useState, useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import { invalidarCache, CLAVE_VINOS } from '../services/cacheDatos';
import { useCart } from '../contexts/CartContext';
import { ReactComponent as DeleteIcon } from '../assets/icons/DeleteIcon.svg';
import { ReactComponent as ArrowIcon } from '../assets/icons/ArrowIcon.svg';
import { CardPayment } from '@mercadopago/sdk-react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { useAuth } from '../contexts/AuthContext';
import {formatCepas, UIDS_CON_ACCESO_A_DEMO, CantidadStepper, obtenerMarcaYVariedad} from './Cupo';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Factura from './Factura';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import LoadingSpinner from '../assets/LoadingSpinner';
import { useToast } from '../contexts/ToastContext';
const storage = getStorage();
// Client ID de PayPal (Sandbox o Live según el que esté configurado en el
// dashboard de PayPal) — nunca el secret, ese solo vive en el backend.
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;
// Silenciado para el lanzamiento (falta vincular la cuenta bancaria de la
// empresa en PayPal para poder retirar la plata): con esto en false, toda
// compra va por Mercado Pago sin importar qué moneda esté mostrando el
// selector — no hace falta tocar nada más para reactivarlo, solo volver a
// poner esto en true cuando esté listo el banco.
const PAYPAL_HABILITADO = false;

const Carrito = () => {
const { cartItems, removeFromCart, updateQuantity, clearCart } = useCart();
const navigate = useNavigate();
const location = useLocation();
// Ref y no state: el origen es el de la entrada al carrito (lo pasa el ícono
// del carrito en el navbar) y no tiene que cambiar en los re-renders.
const origenRef = useRef(location.state?.from);
const [isPaymentReady, setIsPaymentReady] = useState(false);
const { user, loading } = useAuth();
const { showToast } = useToast();
// Misma lógica que el dispatcher de navbar en App.js: PublicNavbar (más
// baja) para Registrado, MemberNavbar (más alta) para Espera/Activo.
const usaNavbarPublica = user?.profileData?.estadoLista === 'Registrado';
const [paymentStatus, setPaymentStatus] = useState(null);
const [isLoading, setIsLoading] = useState(false);
const [paymentType, setPaymentType] = useState('Tarjeta');
const [ventaId, setVentaId] = useState(null);
const [showPaymentForm, setShowPaymentForm] = useState(false);
const [isPdfGenerating, setIsPdfGenerating] = useState(false);
const facturaRef = useRef(null);
// Envuelve solo las 3 copias de la factura (sin el remito) — es lo que se
// arma como PDF aparte para mandarle al cliente por mail.
const facturaSoloRef = useRef(null);
const idemKeyRef = useRef(null);
// Paso previo al Brick: se pide el domicilio de entrega en su propia
// pantalla (con su propio botón "Ir a pagar") y recién cuando está
// confirmado se monta el CardPayment de Mercado Pago. Antes convivían los
// inputs de dirección y el Brick en la misma pantalla, y cada tecla que se
// tipeaba ahí re-renderizaba el Brick (MercadoPago lo toma como cambio de
// props y lo reinicializa solo) — separándolos en dos pasos el Brick ni
// siquiera existe todavía mientras se edita la dirección.
const [direccionConfirmada, setDireccionConfirmada] = useState(false);
// Domicilio de ENTREGA del pedido — separado del domicilio fiscal del
// perfil (ese va en la factura). Se prellena con el del perfil por
// comodidad, pero es editable acá porque un pedido puede mandarse a otro
// lado (regalo, oficina, etc.).
const [direccionEntrega, setDireccionEntrega] = useState({
calle: '', altura: '', piso: '', unidad: '', ciudad: '', codigoPostal: '', pais: '', observaciones: '',
});
useEffect(() => {
if (!user?.profileData) return;
setDireccionEntrega((prev) => ({
calle: user.profileData.calle || '',
altura: user.profileData.altura || '',
piso: user.profileData.piso || '',
unidad: user.profileData.unidad || '',
ciudad: user.profileData.ciudad || '',
codigoPostal: user.profileData.codigoPostal || '',
pais: user.profileData.pais || '',
// No viene del perfil (no hay "observaciones fiscales"): a diferencia del
// resto de los campos, se preserva si el cliente ya había escrito algo acá.
observaciones: prev.observaciones,
}));
}, [user?.profileData]);
const actualizarDireccionEntrega = (campo) => (e) => {
setDireccionEntrega((prev) => ({ ...prev, [campo]: e.target.value }));
};
const direccionEntregaCompleta = Boolean(
direccionEntrega.calle.trim() && direccionEntrega.altura.trim() &&
direccionEntrega.ciudad.trim() && direccionEntrega.codigoPostal.trim() &&
direccionEntrega.pais.trim()
);
// Selector de moneda de visualización (igual que en Druida > Estadísticas):
// solo cambia cómo se MUESTRAN los precios acá. El cobro real a Mercado Pago
// siempre va en ARS — nunca se toca totalCompra/transaction_amount.
const [monedaSeleccionada, setMonedaSeleccionada] = useState('ARS');
const [dolarOficial, setDolarOficial] = useState(null);
const [monedaDropdownAbierto, setMonedaDropdownAbierto] = useState(false);
const monedaDropdownRef = useRef(null);
useEffect(() => {
if (!monedaDropdownAbierto) return undefined;
const cerrarSiEsAfuera = (e) => {
if (monedaDropdownRef.current && !monedaDropdownRef.current.contains(e.target)) {
setMonedaDropdownAbierto(false);
}
};
document.addEventListener('mousedown', cerrarSiEsAfuera);
return () => document.removeEventListener('mousedown', cerrarSiEsAfuera);
}, [monedaDropdownAbierto]);
useEffect(() => {
const fetchCotizaciones = async () => {
try {
const oficialRes = await fetch('https://dolarapi.com/v1/dolares/oficial');
const oficialData = await oficialRes.json();
setDolarOficial(oficialData.venta);
} catch (error) {
console.error('No se pudo obtener la cotización del dólar:', error);
}
};
fetchCotizaciones();
}, []);
// Convierte un nodo del DOM (una o más "hojas" A4 apiladas) en el blob de un
// PDF, cortando la imagen larga en páginas de 297mm cada una.
const generarPdfBlobDesdeNodo = async (nodo) => {
const canvas = await html2canvas(nodo, {
scale: 2,
useCORS: true,
logging: true,
});
const imgData = canvas.toDataURL('image/jpeg', 1.0);
const pdf = new jsPDF({
orientation: 'p',
unit: 'mm',
format: 'a4'
});
const imgWidth = 210;
const pageHeight = 297;
const imgHeight = canvas.height * imgWidth / canvas.width;
let heightLeft = imgHeight;
let position = 0;
pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
heightLeft -= pageHeight;
while (heightLeft >= 0) {
position = heightLeft - imgHeight;
pdf.addPage();
pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
heightLeft -= pageHeight;
}
return pdf.output('blob');
};
// Genera y sube dos PDFs: el completo (3 copias de factura + remito, nuestro
// registro interno, en la ruta de siempre 'facturas/') y uno aparte con solo
// las 3 copias de factura —sin remito—, que es el que se le manda por mail
// al cliente.
const handleGenerateAndUploadPDF = async (newVentaId) => {
if (!facturaRef.current || !facturaSoloRef.current) {
console.error("Referencia de la factura no disponible.");
return {};
}
setIsPdfGenerating(true);
try {
const [pdfCompletoBlob, pdfClienteBlob] = await Promise.all([
generarPdfBlobDesdeNodo(facturaRef.current),
generarPdfBlobDesdeNodo(facturaSoloRef.current),
]);
const fileName = `${newVentaId}.pdf`;
const referenciaCompleta = storageRef(storage, `facturas/${fileName}`);
const referenciaCliente = storageRef(storage, `facturas_cliente/${fileName}`);
await Promise.all([
uploadBytes(referenciaCompleta, pdfCompletoBlob),
uploadBytes(referenciaCliente, pdfClienteBlob),
]);
const [downloadURL, clienteURL] = await Promise.all([
getDownloadURL(referenciaCompleta),
getDownloadURL(referenciaCliente),
]);
console.log('Factura subida con éxito. ID:', newVentaId, 'URL:', downloadURL);

return { downloadURL, clienteURL };
} catch (error) {
console.error('Error al generar o subir el PDF:', error);
return {}; // Vacío si falla
} finally {
setIsPdfGenerating(false);
}
};
if ( loading) {
return (
<div className="historia-container" style={{ textAlign: 'center', padding: '100px 20px' }}>
<div className="titulo-principal">
<hr className="linea-horizontal" />
<h1>CARRITO VACÍO</h1>
<hr className="linea-horizontal" />
</div>
<p className="contenido-centrado" style={{ marginTop: '30px' }}>
Aún no ha añadido ningún producto a su carrito. 🍷
</p>
</div>
);
}
const totalCompra = cartItems.reduce((acc, item) => {
return acc + (item.vino.precioUnidad * item.cantidad);
}, 0);
// Convierte un monto en ARS a la moneda elegida solo para MOSTRARLO.
const convertirAMonedaElegida = (montoEnArs) => {
if (monedaSeleccionada === 'USD_OFICIAL' && dolarOficial) return montoEnArs / dolarOficial;
return montoEnArs;
};
const formatPrice = (price) => {
const prefijo = monedaSeleccionada === 'ARS' ? 'AR$' : 'US$';
const valorConvertido = convertirAMonedaElegida(price);
return `${prefijo} ${new Intl.NumberFormat('es-AR', { maximumFractionDigits: 0 }).format(valorConvertido)}`;
};
const opcionesMoneda = [
{ value: 'ARS', label: 'AR$', disabled: false },
{ value: 'USD_OFICIAL', label: 'US$', disabled: !dolarOficial },
];
const opcionMonedaActual = opcionesMoneda.find(o => o.value === monedaSeleccionada) || opcionesMoneda[0];
// El selector de moneda decide la pasarela real: Pesos paga con Mercado
// Pago, Dólares (cotización oficial — la única con la que se puede pagar,
// nunca el CCL) paga con PayPal — salvo que PAYPAL_HABILITADO esté en
// false, en cuyo caso todo pasa por Mercado Pago sin importar la moneda
// mostrada (Mercado Pago siempre cobra en ARS de todas formas).
const usaPaypal = PAYPAL_HABILITADO && monedaSeleccionada === 'USD_OFICIAL';
const totalBotellas = cartItems.reduce((acc, item) => {
return acc + item.cantidad;
}, 0);
// Shop exige mínimo 6 botellas por compra (combinables entre etiquetas
// distintas); Cupo asignado no tiene ese piso. Se cuenta solo lo que vino
// de Shop (item.vino.origen === 'shop'), marcado al agregarlo al carrito.
const MINIMO_BOTELLAS_SHOP = 6;
const totalBotellasShop = cartItems.reduce((acc, item) => {
return item.vino.origen === 'shop' ? acc + item.cantidad : acc;
}, 0);
const faltanParaMinimoShop = totalBotellasShop > 0 ? Math.max(0, MINIMO_BOTELLAS_SHOP - totalBotellasShop) : 0;
const compraBloqueadaPorMinimo = faltanParaMinimoShop > 0;
// "Volver a la tienda" tiene que respetar de dónde vino el cliente. Lo que
// manda es la ruta desde la que se entró al carrito (el ícono del carrito la
// pasa en location.state.from); si no la tenemos —entrada directa por URL,
// recarga, vuelta desde el pago— caemos al contenido del carrito, y recién
// ahí a /cupo-asignado. Antes se decidía solo por contenido, así que un
// carrito vacío o de cupo mandaba a /cupo-asignado aunque vinieras de Shop
// (y a los que no son 'Activo' esa página los rebota a /perfil).
const destinoPorContenido = cartItems.some(item => item.vino.origen === 'shop') ? '/shop' : '/cupo-asignado';
const origen = origenRef.current;
const destinoTienda = (origen === '/shop' || origen === '/cupo-asignado') ? origen : destinoPorContenido;
const initialization = {
amount: totalCompra,
payer: { email: user?.email },
};
const handleBuyClick = () => {
if (compraBloqueadaPorMinimo) return;
// Compra de prueba para socios/empleados: salta el cobro real (ni Mercado
// Pago ni PayPal), pero corre el resto del flujo normal — venta registrada
// con número real, factura, mail de recibo — para poder probarlo sin gastar
// plata real. El backend además exige que sean productos DEMO-, así que
// nunca toca stock real por más que el UID esté en la lista.
// Igual que un cliente real, primero pasa por el paso de domicilio de
// entrega (showPaymentForm true, direccionConfirmada false) — el disparo de
// procesarCompraDemo() queda en el botón "IR A PAGAR", no acá.
idemKeyRef.current = crypto.randomUUID();
setShowPaymentForm(true);
};

const procesarCompraDemo = async () => {
setIsLoading(true);
try {
const idToken = await user.getIdToken();
const response = await fetch('https://backend-288175208699.europe-west1.run.app/process_payment_demo', {
method: 'POST',
headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
body: JSON.stringify({ items: itemsParaBackend(), direccion_entrega: direccionEntrega }),
});
if (!response.ok) {
const err = await response.json().catch(() => ({}));
throw new Error(err.message || 'No se pudo procesar la compra de prueba.');
}
const data = await response.json();
await manejarRespuestaPago(data);
} catch (error) {
console.error('Error en compra de prueba:', error);
setIsLoading(false);
setPaymentStatus('error');
showToast(error.message || 'No se pudo procesar la compra de prueba.', 'error');
}
};
// Ítems del carrito en el shape que espera el backend, sin importar la
// pasarela (Mercado Pago o PayPal usan el mismo /process_payment vs.
// /paypal/create-order, pero ambos validan sobre esta misma lista).
const itemsParaBackend = () => cartItems.map(item => ({
cantidad: item.cantidad,
idProducto: item.vino.id,
nombreProducto: item.vino.vino_nombre,
precioUnidad: item.vino.precioUnidad,
categoria: item.vino.categoria,
}));

// Post-aprobación, común a Mercado Pago y PayPal: generar PDF, mandar el
// mail de recibo, invalidar caché de stock y mostrar el cartel de éxito.
const manejarRespuestaPago = async (response) => {
    if (response.status === 'approved') {
        const newVentaId = response.venta_id || `temp-${Date.now()}`;

        // flushSync: sin esto, setVentaId dispara un re-render asíncrono y
        // html2canvas podía sacarle la foto al <Factura> ANTES de que el
        // DOM tuviera el ventaId nuevo — la factura salía con el número de
        // comprobante en blanco (caía al placeholder hardcodeado "00000001"
        // en vez del real, ej. "B-00000004").
        flushSync(() => setVentaId(newVentaId));

        const { clienteURL } = await handleGenerateAndUploadPDF(newVentaId);

        if (clienteURL) {
            const emailToken = await user.getIdToken();
            const emailResponse = await fetch('https://backend-288175208699.europe-west1.run.app/api/send-receipt-email', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${emailToken}` },
                body: JSON.stringify({
                    venta_id: newVentaId,
                    // Al cliente le va el PDF sin remito (solo las 3 copias de
                    // la factura); el que tiene el remito de 4ª hoja
                    // ('downloadURL', en 'facturas/') queda como nuestro
                    // registro interno y es el que usa el panel de Ventas.
                    pdf_url: clienteURL,
                    user_uid: user.uid,
                }),
            });
            if (emailResponse.ok) {
                console.log(`Email de recibo con factura lanzado a través de la API.`);
            } else {
                console.error("Fallo al llamar a /api/send-receipt-email.");
            }
        }

        // La compra descontó stock: lo que teníamos guardado de Shop y Cupo
        // asignado quedó viejo de verdad, así que se tira para que la próxima
        // visita a la tienda lo traiga de nuevo.
        invalidarCache(CLAVE_VINOS);
        clearCart();
        setIsLoading(false);
        setPaymentStatus('approved');
    } else {
        setPaymentStatus('rejected');
        setIsLoading(false);
    }
};

// La dirección ya se confirmó en el paso anterior (direccionConfirmada) antes
// de llegar siquiera a montar el Brick, así que acá no hace falta
// revalidarla — solo se manda.
const onSubmit = async (formData) => {
setIsLoading(true);
if (!user) {
setIsLoading(false);
setPaymentStatus('error');
showToast("Debés iniciar sesión para completar la compra.", 'error');
return;
}

const dataToSend = {
...formData,
transaction_amount: totalCompra,
user_uid: user.uid,
idempotency_key: idemKeyRef.current,
items: itemsParaBackend(),
direccion_entrega: direccionEntrega,
};

try {
const idToken = await user.getIdToken();
const response = await fetch('https://backend-288175208699.europe-west1.run.app/process_payment', {
method: 'POST',
headers: {
'Content-Type': 'application/json',
'Authorization': `Bearer ${idToken}`,
},
body: JSON.stringify(dataToSend),
});
if (!response.ok) {
const err = await response.json();
throw new Error(err.message || 'Error en la red o servidor.');
}
const data = await response.json();
await manejarRespuestaPago(data);
} catch (error) {
console.error('Error fatal en el flujo de pago:', error);
setIsLoading(false);
setPaymentStatus('error');
throw error;
}
};

// --- Flujo PayPal (Dólares, cotización oficial) ------------------------
// createOrder: pide al backend que cree la orden (el monto en USD se
// recalcula ahí server-side, nunca se manda desde acá).
const crearOrdenPaypal = async () => {
const idToken = await user.getIdToken();
const response = await fetch('https://backend-288175208699.europe-west1.run.app/paypal/create-order', {
method: 'POST',
headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
body: JSON.stringify({ items: itemsParaBackend(), direccion_entrega: direccionEntrega }),
});
if (!response.ok) {
const err = await response.json().catch(() => ({}));
throw new Error(err.message || 'No se pudo crear la orden de PayPal.');
}
const data = await response.json();
return data.id;
};

// onApprove: el cliente confirmó en PayPal. Recién acá el backend captura el
// pago y, si se aprueba, registra la venta (tipoDivisa: "Dólares").
const aprobarOrdenPaypal = async (data) => {
setIsLoading(true);
try {
const idToken = await user.getIdToken();
const response = await fetch('https://backend-288175208699.europe-west1.run.app/paypal/capture-order', {
method: 'POST',
headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
body: JSON.stringify({ orderID: data.orderID }),
});
if (!response.ok) {
const err = await response.json().catch(() => ({}));
throw new Error(err.message || 'Error al capturar el pago de PayPal.');
}
const result = await response.json();
await manejarRespuestaPago(result);
} catch (error) {
console.error('Error fatal en el flujo de pago con PayPal:', error);
setIsLoading(false);
setPaymentStatus('error');
}
};

const onErrorPaypal = (error) => {
console.log('Error de PayPal:', error);
setIsLoading(false);
setPaymentStatus('error');
};


const onError = async (error) => {
console.log('Error de Brick:', error);
};
const onReady = async () => {
setIsPaymentReady(true);
};
if (paymentStatus === 'approved' || isPdfGenerating) {
if (isPdfGenerating) {
return (
<LoadingSpinner fullScreen />
);
}
return (
<div className="historia-container" style={{ textAlign: 'center', padding: '100px 20px' }}>
<div className="titulo-principal">
<hr className="linea-horizontal" />
<h1>¡PAGO APROBADO!</h1>
<hr className="linea-horizontal" />
</div>
<p className="contenido-centrado" style={{ marginTop: '30px' }}>
Tu compra fue exitosa.
<br />
¡Gracias por elegirnos!
</p>
</div>
);
}
return (
<div className="historia-container" style={{height:'fit-content', marginTop:'40px'}}>
<div style={{position:'absolute',zIndex:'-999999',left:'-9999px'}}>
<div style={{
position: 'relative',
width: '210mm',
// 4 hojas A4 apiladas: 3 copias de factura + el remito.
height: '1180mm',
overflowY: 'auto'
}}>
<Factura
ref={facturaRef}
refSoloFacturas={facturaSoloRef}
datosCliente={user?.profileData || {}}
productosVendidos={cartItems.map(item => ({ 
idProducto: item.vino.id,
nombreProducto: item.vino.vino_nombre,
cantidad: item.cantidad,
precioUnidad: item.vino.precioUnidad,
tipoUnidad: 'u',
}))}
tipoFactura="B"
totalNeto={totalCompra / 1.21}
iva21={totalCompra * 0.21 / 1.21}
iva10_5={0} 
totalFinal={totalCompra}
numeroComprobante={ventaId}
modalidadPago={paymentType}
datosCheque={null}
/>
</div></div>
{/* Mientras se está en la pantalla de pago (ya se eligió MercadoPago o
    PayPal), la flecha no puede mandarte afuera del carrito: si alguien
    entró por PayPal y se arrepiente, tiene que poder volver a elegir Pesos
    sin perder el carrito. Solo navega a la tienda antes de llegar ahí. */}
<Link
to={destinoTienda}
className="carrito-volver-tienda"
style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center'}}
onClick={showPaymentForm ? (e) => {
e.preventDefault();
// En el paso del Brick, la flecha vuelve al domicilio (no tira todo el
// carrito), igual que el botón "Volver" de al lado del botón Pagar.
if (direccionConfirmada) {
setDireccionConfirmada(false);
return;
}
setShowPaymentForm(false);
setPaymentStatus(null);
setIsPaymentReady(false);
} : undefined}
>
<div className={`navegacion-flecha flecha-izquierda carrito-flecha${usaNavbarPublica ? ' carrito-flecha-registrado' : ''}`}
style={{ margin:'0', left:'50px'}}
><div>
<ArrowIcon className='arrow-icon' style={{height:'15px',overflow:'visible', width:'auto',margin:'auto 0'}}/></div>
<span className="navegacion-texto">
{!showPaymentForm ? 'Volver a la tienda' : direccionConfirmada ? 'Volver a domicilio' : 'Volver al carrito'}
</span>
</div>
</Link>
{paymentStatus !== 'approved' && (
<div className="titulo-principal">
<hr className="linea-horizontal" />
<h1>CESTA DE COMPRAS</h1>
<hr className="linea-horizontal" />
</div>
)}
{!showPaymentForm && (
<>
<div className="carrito-items-list">
{}
<div className="headerRow">
<p className='header-item-count' style={{ flex: '4', fontWeight: 'bold' }}>{totalBotellas} ITEMS</p>
<p className='header-quantity' style={{ flex: '1', textAlign: 'center' }}>CANTIDAD</p>
<p className='header-total' style={{ flex: '1.4', textAlign: 'right' }}>TOTAL</p>
</div>
{cartItems.map(item => {
const { marca: marcaVino, resto: restoVino } = obtenerMarcaYVariedad(item.vino);
const cepasFormateadas = formatCepas(item.vino.cepas);
const contenidoCepas = item.vino.tipo_vino === 'Crema de Estrellas'
? 'Crema de Estrellas'
: (cepasFormateadas && cepasFormateadas.length > 0 ? cepasFormateadas.join(' • ') : null);
// Mismo tope que en Cupo asignado/Shop: stock, o el cupo personal si el
// vino tiene uno asignado (los de Shop nunca lo tienen). Esto es solo la
// traba de interfaz — la que de verdad importa es la del backend.
const cupoPersonal = item.vino.cupoAsignado;
const maximoPermitido = (cupoPersonal !== null && cupoPersonal !== undefined)
? Math.min(item.vino.cantidadUnidades, cupoPersonal)
: item.vino.cantidadUnidades;
const handleCantidadChange = (valor) => {
let cantidad = parseInt(valor);
if (isNaN(cantidad) || cantidad < 1) cantidad = 1;
if (cantidad > maximoPermitido) cantidad = maximoPermitido;
updateQuantity(item.vino.id, cantidad);
};
return (
<div key={item.vino.id} className="carrito-item-card">
{}
<div className="productColumn">
{item.vino.etiqueta_imagen_url && (
<div className='image-wrapper'> {}
<img 
src={item.vino.etiqueta_imagen_url} 
alt={item.vino.vino_nombre} 
className="image" 
/>
</div>
)}
<div className="info">
{}
<h3 className="title" style={{fontWeight:'bold', textTransform:'uppercase', fontSize:'clamp(13px, 4vw, 17px)'}}>{marcaVino}</h3>
{restoVino && <p className='cepas-anada'>{restoVino}</p>}
{contenidoCepas && <p className='cepas-anada'>{contenidoCepas}</p>}
{item.vino.anada && <p className='cepas-anada'>{item.vino.anada}</p>}
{}
<p className="unit-price">
{formatPrice(item.vino.precioUnidad)}
</p>
</div>
</div>
{}
<div className="quantityColumn">
<CantidadStepper
min={1}
max={maximoPermitido}
value={item.cantidad}
onChange={handleCantidadChange}
anchoDinamico
/>
</div>
{}
{}
<div className="totalColumn" style={{position:'relative'}}>
<p className="totalValue">{formatPrice(item.vino.precioUnidad * item.cantidad)}</p>
</div>
<div onClick={() => removeFromCart(item.vino.id)}  className='delete-button'> {}
<DeleteIcon style={{height:'17px'}}/>
</div>
{}
</div>
);
})}
</div>
<div className="carrito-summary">
<h2 className="summaryTitle" style={{textTransform:'none'}}>Subtotal ({totalBotellas}) </h2>
<h2 className="summaryTitle">{formatPrice(totalCompra)}</h2>
</div>
{compraBloqueadaPorMinimo && (
<p className="carrito-minimo-shop">
Shop exige un mínimo de {MINIMO_BOTELLAS_SHOP} botellas por compra.
</p>
)}
<div className="carrito-moneda-row">
<div className="carrito-moneda-selector">
<span>Valores expresados en:</span>
<div className="carrito-moneda-dropdown" ref={monedaDropdownRef}>
<button
type="button"
className="carrito-moneda-trigger"
onClick={() => setMonedaDropdownAbierto(o => !o)}
>
<span>{opcionMonedaActual.label}</span>
<span className="carrito-moneda-chevron" />
</button>
{monedaDropdownAbierto && (
<ul className="carrito-moneda-lista">
{opcionesMoneda.map(o => (
<li key={o.value}>
<button
type="button"
disabled={o.disabled}
className={o.value === monedaSeleccionada ? 'carrito-moneda-opcion activa' : 'carrito-moneda-opcion'}
onClick={() => {
setMonedaSeleccionada(o.value);
setMonedaDropdownAbierto(false);
}}
>
{o.label}
</button>
</li>
))}
</ul>
)}
</div>
</div>
{/* PROTOTIPO: si usaPaypal, en vez de "COMPRAR" (que llevaría a la pantalla
    intermedia con el aviso rojo) se muestra el botón de PayPal directo acá
    mismo, un solo click abre el popup. Es exploratorio — si se valida, el
    branch usaPaypal de más abajo (dentro de showPaymentForm) queda
    inalcanzable para PayPal y se puede limpiar. */}
{usaPaypal ? (
// Ancho por clase CSS (.carrito-paypal-boton en App.css), no inline: así
// tiene su propia media query para achicarse en mobile, algo que un
// style={{width}} fijo no puede hacer sin JS.
<div className="carrito-paypal-boton">
<PayPalScriptProvider options={{ clientId: PAYPAL_CLIENT_ID, currency: 'USD', 'disable-funding': 'card' }}>
<PayPalButtons
  style={{ layout: 'vertical' }}
  disabled={compraBloqueadaPorMinimo}
  forceReRender={[totalCompra]}
  createOrder={crearOrdenPaypal}
  onApprove={aprobarOrdenPaypal}
  onError={onErrorPaypal}
  onCancel={() => setIsLoading(false)}
/>
</PayPalScriptProvider>
</div>
) : (
<button
className="add-to-cart-button"
onClick={handleBuyClick}
disabled={compraBloqueadaPorMinimo}
style={compraBloqueadaPorMinimo ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
>
COMPRAR
</button>
)}
</div>
</>
)}
{}
{showPaymentForm && paymentStatus !== 'approved' && (
<div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
{/* max-width fijo: sin esto hereda el 45% variable de .historia-container,
    así que el Brick/los botones de PayPal se agrandaban o achicaban según
    el ancho de la ventana en vez de tener un tamaño consistente. */}
{/* Sin maxWidth propio: hereda el 100% de .historia-container (45% del
    viewport en desktop, ver App.css), igual que el resto del contenido de
    la página (carrito, resumen) — antes quedaba angosto a propósito (420px)
    porque convivía con más columnas al lado; ya no aplica. */}
<div className='card-payment-container' style={{ width: '100%' }}>

{(isLoading || isPdfGenerating) ? (
<LoadingSpinner fullScreen />
) : !direccionConfirmada ? (
// PASO 1: domicilio de entrega, en su propia pantalla — el Brick de
// Mercado Pago ni se monta todavía, así que tipear acá no lo toca para nada.
<>
<p style={{ marginBottom: '10px', fontSize: '16px' }}>Domicilio de entrega</p>
<div style={{ display: 'flex', gap: '20px', marginBottom: '20px' }}>
<input
  type="text" placeholder="Calle" value={direccionEntrega.calle}
  onChange={actualizarDireccionEntrega('calle')}
  style={{ flex: 2, minWidth: 0, padding: '10px', border: '1px solid #a58d70', boxSizing: 'border-box' }}
/>
<input
  type="text" placeholder="Altura" value={direccionEntrega.altura}
  onChange={actualizarDireccionEntrega('altura')}
  style={{ flex: 1, minWidth: 0, padding: '10px', border: '1px solid #a58d70', boxSizing: 'border-box' }}
/>
</div>
<div style={{ display: 'flex', gap: '20px', marginBottom: '20px' }}>
<input
  type="text" placeholder="Piso (opcional)" value={direccionEntrega.piso}
  onChange={actualizarDireccionEntrega('piso')}
  style={{ flex: 1, minWidth: 0, padding: '10px', border: '1px solid #a58d70', boxSizing: 'border-box' }}
/>
<input
  type="text" placeholder="Depto/Unidad (opcional)" value={direccionEntrega.unidad}
  onChange={actualizarDireccionEntrega('unidad')}
  style={{ flex: 1, minWidth: 0, padding: '10px', border: '1px solid #a58d70', boxSizing: 'border-box' }}
/>
</div>
<div style={{ display: 'flex', gap: '20px', marginBottom: '20px' }}>
<input
  type="text" placeholder="Ciudad/Localidad/Provincia" value={direccionEntrega.ciudad}
  onChange={actualizarDireccionEntrega('ciudad')}
  style={{ flex: 2, minWidth: 0, padding: '10px', border: '1px solid #a58d70', boxSizing: 'border-box' }}
/>
<input
  type="text" placeholder="Código postal" value={direccionEntrega.codigoPostal}
  onChange={actualizarDireccionEntrega('codigoPostal')}
  style={{ flex: 1, minWidth: 0, padding: '10px', border: '1px solid #a58d70', boxSizing: 'border-box' }}
/>
</div>
<div style={{ display: 'flex', gap: '20px' }}>
<input
  type="text" placeholder="País" value={direccionEntrega.pais}
  onChange={actualizarDireccionEntrega('pais')}
  style={{ flex: 1, minWidth: 0, padding: '10px', border: '1px solid #a58d70', boxSizing: 'border-box' }}
/>
</div>
<textarea
  placeholder="Observaciones (opcional): ej. no anda el timbre"
  value={direccionEntrega.observaciones}
  onChange={actualizarDireccionEntrega('observaciones')}
  rows={2}
  style={{ width: '100%', marginTop: '20px', padding: '10px', border: '1px solid #a58d70', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'MinervaModern', fontWeight: 'normal', fontSize: '18px' }}
/>
{!direccionEntregaCompleta && (
<p style={{ color: '#a00', fontSize: '13px', marginTop: '6px' }}>
Completá calle, altura, ciudad, código postal y país para poder pagar.
</p>
)}
<button
  className="add-to-cart-button carrito-ir-a-pagar-boton"
  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '38px', padding: '0 40px', boxSizing: 'border-box', marginTop: '16px', marginLeft: 'auto' }}
  disabled={!direccionEntregaCompleta}
  onClick={() => {
    // Socios/empleados (UIDS_CON_ACCESO_A_DEMO): ya completaron el domicilio,
    // así que acá se dispara directo la compra de prueba (sin Brick, sin
    // cobro real) en vez de pasar al paso 2.
    if (UIDS_CON_ACCESO_A_DEMO.includes(user?.uid)) {
      procesarCompraDemo();
      return;
    }
    setDireccionConfirmada(true);
  }}
>
IR A PAGAR
</button>
</>
) : (
// PASO 2: Mercado Pago / PayPal — la dirección ya quedó confirmada arriba.
<>
{paymentStatus === 'rejected' && <p style={{color: 'red', textAlign: 'center'}}>El pago fue rechazado. Por favor, verifica tus datos o intenta con otro medio de pago.</p>}
{paymentStatus === 'error' && <p style={{color: 'red', textAlign: 'center'}}>Hubo un error al procesar el pago. Intenta de nuevo.</p>}
{!isPaymentReady && !usaPaypal && (
<LoadingSpinner fullScreen />
)}
<div style={{ display: (usaPaypal || (isPaymentReady && !isLoading)) ? 'block' : 'none' }}>
<div style={{ display: (usaPaypal || (isPaymentReady && !isLoading)) ? 'block' : 'none' }}>
{usaPaypal ? (
<>
{/* #a00: mismo rojo que .login-error-message en App.css, para que los
    avisos de error/advertencia sean consistentes en todo el sitio. */}

{/* PayPalScriptProvider no tiene tamaño propio (solo da contexto); el que
    puede quedar descentrado es el PayPalButtons que dibuja el SDK adentro
    — este wrapper lo centra pase lo que pase con el ancho que calcule. */}
<div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
{/* disable-funding: 'card' saca el botón "Debit or Credit Card" (guest
    checkout con tarjeta) del set de Smart Buttons, dejando solo PayPal. */}
<PayPalScriptProvider options={{ clientId: PAYPAL_CLIENT_ID, currency: 'USD', 'disable-funding': 'card' }}>
<PayPalButtons
  style={{ layout: 'vertical' }}
  disabled={isLoading}
  forceReRender={[totalCompra]}
  createOrder={crearOrdenPaypal}
  onApprove={aprobarOrdenPaypal}
  onError={onErrorPaypal}
  onCancel={() => setIsLoading(false)}
/>
</PayPalScriptProvider>
</div>
</>
) : (
<CardPayment
  initialization={initialization}
  onSubmit={onSubmit}
  onReady={onReady}
  onError={onError}
customization={{
  visual: {
    hideFormTitle: true,
    theme: 'default',
    hideEmailField: true,
    style: {
      customVariables: {
        baseColor: "#a58d70",
        baseColorFirstVariant: "#a58d70",
        buttonTextColor: "#ffffff",

        outlinePrimaryColor: "#a58d70",
        errorColor: "#2d1a3b",

        inputFocusedBoxShadow: "0 0 0 1px #a58d70",
        inputErrorFocusedBoxShadow: "0 0 0 1px #2d1a3b",
        inputBorderWidth: "1px",
        inputFocusedBorderWidth: "1px",

        fontWeightNormal: "400",
        fontWeightSemiBold: "400",
        borderRadiusMedium: "0",
        textPrimaryColor: "#1a1a1a",
        fontSizeMedium: "16px",

        inputBackgroundColor: "#ffffff",
        formBackgroundColor: "#ffffff",
        formInputsTextTransform: "none",
        formPadding: "0",

        fontSizeExtraSmall: "16px",
        fontSizeSmall: "16px",
        fontSizeLarge: "16px",
        fontSizeExtraLarge: "16px",
      },
    },
  },
  paymentMethods: {
    maxInstallments: 1,
  },
}}
/>
)}
</div>
</div>
</>
)}
</div>
</div>
)}
</div>
);
};
export default Carrito;